import {
  MenuItem,
  QuantityPicker,
  Typography,
  DropdownField,
  Alert,
} from '@one-thd/sui-atomic-components';
import React, { useEffect, useState } from 'react';
import { setSelectedItems, useReturnStore } from '@src/stores/returnStore';
import { FINALIZE_RETURN_STEP } from '@src/utils/returnFlow';
import { useNewFlowStore } from '@src/stores/newFlowStore';
import {
  onUpdateReason,
  useErrorQtyReasonStore,
} from '@src/stores/errors/errorQtyReasonStore';
import { MAX_WEIGHT_LIMIT, RETURN_METHOD } from '@src/constants/general';
import { getFeatureSwitchStatic } from '@src/utils/general';
import { returnReasonCodes } from '@src/constants/index';
import { ItemToReturn } from '@src/models/returns';
import ItemImage from '@src/components/shared/items/ItemImage';
import ItemDescription from '@src/components/shared/items/ItemDescription';
import { getItemEligibilities } from '@src/utils/eligibility-utils';

interface QuantityItemProps {
  item: ItemToReturn;
  selectedItems: ItemToReturn[];
  isHrtvIneligible: boolean;
  setHasWeightError?: (hasError: string | null) => void;
  hasWeightError?: string | null;
}

export const QuantityItem = ({
  item,
  selectedItems,
  isHrtvIneligible,
  setHasWeightError,
  hasWeightError,
}: QuantityItemProps) => {
  const enableAggregateWeightPickup = getFeatureSwitchStatic(
    'enableAggregateWeightPickup',
  );
  const returnEligibilityDetails = useReturnStore(
    (state) => state.returnEligibilityDetails,
  );
  const orderDetails = useReturnStore((state) => state.orderDetailsV2);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );
  const isApoFpo = useReturnStore((state) => state.isApoFpo);
  const originalItem = orderDetails?.lineItems.find(
    (lineItem) => lineItem.lineId === item.lineItemId,
  );
  const originalEligibilities = getItemEligibilities(
    originalItem,
    returnEligibilityDetails,
    isMilitaryExchangeGuest && Boolean(isApoFpo),
  );

  const returnReasons = Object.keys(returnReasonCodes);
  const hasReasonError = useErrorQtyReasonStore(
    (state) => state.idsWithErrors,
  ).includes(item.lineItemId || '');
  const [itemToReturnState, setItemToReturnState] = useState<ItemToReturn>(
    selectedItems.find((i) => i.lineItemId === item.lineItemId) as ItemToReturn,
  );
  const currentStep = useNewFlowStore((state) => state.currentStep);
  const isReviewStep = currentStep === FINALIZE_RETURN_STEP;
  const isDrawer = currentStep === 'FinalizeReturn';

  const handleQuantityChange = (amount: number | string) => {
    const amountInt =
      typeof amount === 'string' ? parseInt(amount || '0', 10) : amount;
    const totalWeight = (item.itemWeight || 0) * amountInt;

    // Validation gate for UPS Drop-Off/ LORP edit drawer mode
    if (isDrawer && enableAggregateWeightPickup) {
      if (
        item.returnMethod === RETURN_METHOD.UPS_DROP_OFF &&
        totalWeight >= MAX_WEIGHT_LIMIT
      ) {
        setHasWeightError?.(
          'Total weight cannot exceed 150 pounds for UPS Drop-Off.',
        );
      } else if (
        item.returnMethod === RETURN_METHOD.LORP &&
        totalWeight < MAX_WEIGHT_LIMIT &&
        !item.hazmatItem
      ) {
        setHasWeightError?.(
          'Total weight must be 150 pounds or more for Return Pickup. Quantity does not meet weight requirements.',
        );
      } else {
        setHasWeightError?.(null);
      }
    }

    if (
      !Number.isNaN(amountInt) &&
      amountInt >= 0 &&
      amountInt <= item.currentQuantity
    ) {
      setItemToReturnState(() => ({
        ...itemToReturnState,
        returnQuantity: amountInt,
      }));

      // Only run dynamic eligibility update in quantity picker page (not in edit drawer)
      if (!isDrawer) {
        handleWeightLimit(amountInt);
      }
    }
  };

  const handleWeightLimit = (amountInt: number) => {
    // if FS is off do nothing
    if (!enableAggregateWeightPickup) return;
    if (item.hazmatItem) {
      // hazmat items cannot be UPS Drop Off
      setItemToReturnState((state) => ({
        ...state,
        eligibilities: {
          ...originalEligibilities,
          [RETURN_METHOD.UPS_DROP_OFF]: false,
        },
      }));
      return;
    }

    const totalItemWeight = (item.itemWeight || 0) * amountInt;

    const exceedsWeightLimit = totalItemWeight >= MAX_WEIGHT_LIMIT;
    if (exceedsWeightLimit) {
      // over weight limit, cannot do UPS Drop Off
      setItemToReturnState((state) => ({
        ...state,
        eligibilities: {
          ...originalEligibilities,
          [RETURN_METHOD.UPS_DROP_OFF]: false,
        },
      }));
    } else {
      // under weight limit, cannot do LORP
      setItemToReturnState((state) => ({
        ...state,
        eligibilities: {
          ...originalEligibilities,
          [RETURN_METHOD.LORP]: false,
        },
      }));
    }
  };

  const onReasonCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const reasonText = event.target.value;
    onUpdateReason(item.lineItemId || '');
    setItemToReturnState(() => ({
      ...itemToReturnState,
      returnReasonCode: returnReasonCodes[reasonText],
      returnReasonText: reasonText,
    }));
  };

  const updateSelectedReturnItems = (updatedItem: ItemToReturn) => {
    const updatedItems = selectedItems.map((i) =>
      i.lineItemId === updatedItem.lineItemId ? updatedItem : i,
    );
    setSelectedItems(updatedItems);
  };

  useEffect(() => {
    updateSelectedReturnItems(itemToReturnState);
  }, [itemToReturnState]);

  useEffect(() => {
    // on mount, check weight limit based on initial quantity
    handleWeightLimit(itemToReturnState.returnQuantity);
  }, [enableAggregateWeightPickup, orderDetails]);

  const getQuantityValue = () => {
    if (isHrtvIneligible) return 0;
    return itemToReturnState.returnQuantity;
  };

  const renderReturnReason = () => {
    if (isReviewStep) {
      // Return reason will not be editable in review step
      return (
        <>
          <Typography weight="bold">Return Reason*</Typography>
          <Typography>{itemToReturnState.returnReasonText}</Typography>
        </>
      );
    }
    return (
      <DropdownField
        fullWidth
        id={`return-reason-dropdown-${item.lineItemId}`}
        label="Return Reason"
        labelProps={{ required: true }}
        disabled={isHrtvIneligible}
        value={itemToReturnState.returnReasonText}
        statusMessage={
          hasReasonError
            ? 'Please select a return reason in order to proceed.'
            : undefined
        }
        status={hasReasonError ? 'error' : undefined}
        onChange={onReasonCodeChange}
      >
        {returnReasons.map((reasonText: string, index: number) => (
          <MenuItem key={index} value={reasonText}>
            {reasonText}
          </MenuItem>
        ))}
      </DropdownField>
    );
  };

  return (
    <div
      key={item.sku}
      className={`sui-flex sui-flex-col sui-full sui-gap-6 ${
        isDrawer ? 'sui-py-2' : ''
      }`}
      data-testid={isDrawer ? 'quantity-item-drawer' : 'item'}
    >
      <div className="sui-flex sui-flex-row sui-items-center">
        <ItemImage
          itemDescription={item?.itemDescription}
          imageUrl={item?.itemImageURL}
        />
        <ItemDescription
          brandName={item?.brandName}
          itemDescription={item?.itemDescription}
          fullWidth
        />
      </div>
      <div className="sui-flex sui-flex-col sui-gap-4">
        <div className="sui-flex sui-flex-row sui-items-start sui-justify-between">
          <QuantityPicker
            label={`Return Qty (of ${item.currentQuantity})`}
            min={1}
            max={item.currentQuantity}
            onChange={handleQuantityChange}
            value={getQuantityValue()}
            disabled={isHrtvIneligible}
            disableDefaultStatus={isDrawer && Boolean(hasWeightError)}
            minimumMessage={isDrawer ? 'Minimum of 1' : undefined}
            maximumMessage={
              isDrawer ? `Maximum of ${item.currentQuantity}` : undefined
            }
          />
          <Typography weight="bold" height="tight">
            ${(item.itemCost * itemToReturnState.returnQuantity).toFixed(2)}
          </Typography>
        </div>
        {hasWeightError && (
          <div className="sui-max-h-[50px] sui-mb-2">
            <Alert status="error" size="small">
              <Typography size="sm">{hasWeightError}</Typography>
            </Alert>
          </div>
        )}
      </div>
      <div className="sui-flex sui-flex-col sui-items-start sui-max-w-[450px] sm:sui-max-w-[342px]">
        {renderReturnReason()}
      </div>
    </div>
  );
};
